<script>
import DataLayer from '@services/data-layer/index'
import SharedCarousel from '@components/carousel/shared-carousel'
import SharedCarouselSlide from '@components/carousel/shared-carousel-slide'
import Layout from '@layouts/main'
import MixGridBlock from '@components/mix-grid-block'
import MixGridTile from '@components/mix-grid-tile'
import MixTableBlock from '@components/mix-table-block.v2'
import ArtistBlock from '@components/artist/artist-block'
import GenreHero from './genre-hero'

export default {
    page() {
        const title = this.genre?.name ? `${this.genre.name} Mixes` : 'Genre Mixes'
        const description = this.genre?.name
            ? `${this.genre.name} mixes from global underground artists`
            : 'genre mixes from global underground artists'
        const image = `${process.env.VUE_APP_CLIENT_URL}/frisky-social-share-image.png`
        const canonicalUrl = `https://frisky.fm${this.$route.path}`
        return {
            title,
            link: [{ rel: 'canonical', vmid: 'canonical', href: canonicalUrl }],
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },
                { property: 'og:image', vmid: 'og:image', content: image },
                { property: 'og:type', vmid: 'og:type', content: 'website' },
                { property: 'og:url', vmid: 'og:url', content: canonicalUrl },
                { property: 'og:site_name', vmid: 'og:site_name', content: 'FRISKY' },

                { name: 'twitter:card', vmid: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', vmid: 'twitter:title', content: title },
                { name: 'twitter:description', vmid: 'twitter:description', content: description },
                { name: 'twitter:image', vmid: 'twitter:image', content: image },
                { name: 'twitter:url', vmid: 'twitter:url', content: canonicalUrl },
            ],
        }
    },
    components: {
        Layout,
        MixGridBlock,
        MixGridTile,
        MixTableBlock,
        ArtistBlock,
        GenreHero,
        SharedCarousel,
        SharedCarouselSlide,
    },
    data() {
        return {
            allPopularMixes: Array.from(Array(5), (e, i) => ({
                id: i,
                genre: [],
            })),
            latestMixes: Array.from(Array(10), (e, i) => ({
                id: i,
                genre: [],
            })),
            artists: Array.from(Array(10), (e, i) => ({
                id: i,
            })),
            trendingMixes: [],
            INITIAL_POPULAR_MIXES: 5,
            visiblePopularMixes: 5,
            INITIAL_LATEST_MIXES: 12,
            visibleLatestMixes: 12,
            genre: null,
            errorMessage: null,
        }
    },
    computed: {
        popularMixes() {
            return this.allPopularMixes.slice(0, this.visiblePopularMixes)
        },
        trendingTitle() {
            return `Trending in ${this.genre?.name || ''}`.trim()
        },
        displayedTrendingMixes() {
            return this.trendingMixes.slice(0, 6)
        },
    },
    async mounted() {
        try {
            const genreRes = await DataLayer.genres.getBySlug({ slug: this.$router.currentRoute.params.slug })
            this.genre = genreRes.body

            Promise.all([
                DataLayer.mixes.getPopularByGenre(this.genre.name),
                DataLayer.mixes.getLatestByGenre(this.genre.name),
                DataLayer.artists.getRandomByGenre(this.genre.name),
                DataLayer.mixes.getTrendingByGenre(this.genre.slug),
            ]).then(([popularMixes, latestMixes, artists, trendingMixes]) => {
                this.allPopularMixes = popularMixes.body
                this.latestMixes = latestMixes.body
                this.artists = artists.body
                this.trendingMixes = trendingMixes.body
            }).catch(_error => {
                this.errorMessage = 'Unable to load genre content. Please try again later.'
            })
        } catch (error) {
            this.errorMessage = 'Unable to load genre. Please try again later.'
        }
    },
    methods: {
        showMorePopularMixes() {
            this.visiblePopularMixes *= 2
        },
        showMoreLatestMixes() {
            DataLayer.mixes
                .getLatestByGenre(this.genre.name, this.INITIAL_LATEST_MIXES, this.visibleLatestMixes)
                .then(response => {
                    this.latestMixes.push(...response.body)
                    if (response.body.length === 0) {
                        this.INITIAL_LATEST_MIXES -= 1
                    }
                })
            this.visibleLatestMixes += this.INITIAL_LATEST_MIXES
        },
    },
}
</script>

<template>
    <Layout>
        <GenreHero
            :text="genre?.name || ''"
            :resource="genre"
            class="hero"
        />
        <BaseContent>
            <BaseSection v-if="errorMessage">
                <div class="error-message">{{ errorMessage }}</div>
            </BaseSection>
            <BaseSection
                v-if="displayedTrendingMixes.length"
                :title="trendingTitle"
                padded
            >
                <SharedCarousel
                    :per-page-custom="[[0, 2], [768, 3], [1025, 6]]"
                    :pagination-enabled="false"
                    :scroll-per-page="false"
                    :min-swipe-distance="100"
                    :space-padding="0"
                    :mouse-drag="displayedTrendingMixes.length > 1"
                    class="carousel"
                >
                    <SharedCarouselSlide
                        v-for="(mix, index) in displayedTrendingMixes"
                        :key="`genre-trending-${mix.id}-${index}`"
                        class="slide"
                    >
                        <MixGridTile
                            :mix="mix"
                            :index="index"
                        />
                    </SharedCarouselSlide>
                </SharedCarousel>
            </BaseSection>
            <BaseSection
                v-if="popularMixes && popularMixes.length > 0"
                title="Popular Mixes"
                short
                padded
            >
                <MixTableBlock
                    :mixes="popularMixes"
                    :buttons="{ playCount: true }"
                    genres
                    is-indexed-list
                />
                <BaseLoadMore
                    :id="genre?.name"
                    :list="popularMixes"
                    :visible="5"
                    :more="5"
                    :max="10"
                    :get-fc="showMorePopularMixes"
                    :update-fc="() => {}"
                />
            </BaseSection>
            <BaseSection
                v-if="latestMixes && latestMixes.length > 0"
                title="Mixes"
                padded
            >
                <MixGridBlock :mixes="latestMixes" />
                <BaseLoadMore
                    :id="genre?.name"
                    :list="latestMixes"
                    :visible="12"
                    :more="12"
                    :get-fc="showMoreLatestMixes"
                    :update-fc="() => {}"
                />
            </BaseSection>
            <BaseSection
                v-if="artists && artists.length > 0"
                title="Artists"
            >
                <ArtistBlock
                    :artists="artists"
                    wrapping
                    padded-left-mobile
                />
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.hero {
    margin-bottom: $s-xxl;
}

.carousel {
    position: relative;
    margin: 0 (-$size-grid-padding);
    overflow: visible;
}

.slide {
    position: relative;
    padding: 0 $size-grid-padding;

    &:hover,
    &:focus-within {
        z-index: 2;
    }
}

:deep(.carousel .sharedCarouselViewport),
:deep(.carousel .swiper-wrapper),
:deep(.carousel .sharedCarouselSlide) {
    overflow: visible;
}

.error-message {
    padding: $s-l;
    margin-bottom: $s-l;
    color: $c-white;
    background-color: rgba($c-red, 0.2);
    border: 1px solid $c-red;
    border-radius: 4px;
    text-align: center;
}
</style>
